import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { listCurrentUserOrders, payOrder, getOrderById } from '@/services/orders';
import { requireSupabase } from '@/lib/supabase';
import type { Order } from '@/domain/types';
import { formatDate } from '@/lib/format';

export default function DashboardOrders() {
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState('all');
  const [userOrders, setUserOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [payingOrderId, setPayingOrderId] = useState<string | null>(null);
  const [payError, setPayError] = useState('');
  const [payModalOrder, setPayModalOrder] = useState<Order | null>(null);
  const [detailOrder, setDetailOrder] = useState<Order | null>(null);

  const openDetail = async (ord: Order) => {
    const full = await getOrderById(ord.id);
    if (full) setDetailOrder(full);
  };

  const downloadInvoice = (ord: Order) => {
    const invoice = `
MOTIONSDIO STORE
${ord.orderRef}
Date: ${formatDate(ord.createdAt)}
Status: ${ord.status}

Product: ${ord.productName}
Plan: ${ord.planName}
Amount: $${ord.amount}
${ord.paidAt ? `Paid: ${formatDate(ord.paidAt)}` : ''}

Thank you for your purchase!
${window.location.origin}
`.trim();
    const blob = new Blob([invoice], { type: 'text/plain' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `invoice-${ord.orderRef}.txt`; a.click();
  };

  // Check for NOWPayments redirect - verify payment via API
  useEffect(() => {
    const npId = searchParams.get('NP_id');
    if (!npId || userOrders.length === 0) return;
    // Verify the payment with NOWPayments API
    fetch(`https://api.nowpayments.io/v1/invoice/${npId}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.payment_status === 'finished' || data.payment_status === 'confirmed') {
          const matching = userOrders.find((o) => o.orderRef === data.order_id && o.status === 'pending');
          if (matching) {
            payOrder(matching.id).then(() => {
              setUserOrders((prev) => prev.map((o) => o.id === matching.id ? { ...o, status: 'paid' as const } : o));
            }).catch(() => {});
          }
        }
      }).catch(() => {});
  }, [searchParams, userOrders]);

  const openPayModal = (ord: Order) => {
    setPayModalOrder(ord);
    setPayError('');
    setPayingOrderId(null);
  };

  const handlePayNow = async () => {
    if (!payModalOrder) return;
    setPayingOrderId(payModalOrder.id);
    setPayError('');

    try {
      // Try to redirect to NOWPayments first (crypto orders)
      const supabase = requireSupabase();
      const token = (await supabase.auth.getSession()).data.session?.access_token;

      if (token) {
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        if (!supabaseUrl) throw new Error('Supabase URL not configured.');
        const res = await fetch(
          `${supabaseUrl}/functions/v1/create-crypto-payment`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            body: JSON.stringify({
              orderRef: payModalOrder.orderRef,
              amount: payModalOrder.amount,
              productName: payModalOrder.productName,
              planName: payModalOrder.planName,
            }),
          },
        );
        const data = await res.json();
        if (res.ok && data.paymentUrl) {
          window.location.href = data.paymentUrl;
          return;
        }
      }
    } catch {
      // Fall through to direct pay if NOWPayments fails
    }

    // Fallback: mark as paid directly
    try {
      await payOrder(payModalOrder.id);
      setUserOrders((prev) =>
        prev.map((o) =>
          o.id === payModalOrder.id ? { ...o, status: 'paid' as const, paidAt: new Date().toISOString() } : o,
        ),
      );
      setPayModalOrder(null);
    } catch (err: unknown) {
      setPayError(err instanceof Error ? err.message : 'Payment failed.');
    } finally {
      setPayingOrderId(null);
    }
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    listCurrentUserOrders()
      .then((data) => { if (mounted) setUserOrders(data); })
      .catch(() => { if (mounted) setUserOrders([]); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const filtered = filter === 'all' ? userOrders : userOrders.filter((o) => o.status === filter);

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-surface-900">My Orders</h1>
          <p className="text-sm text-surface-500 mt-1">View and manage your purchase history.</p>
        </div>
        <div className="flex items-center gap-2">
          {['all', 'paid', 'pending'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
                filter === f
                  ? 'bg-brand-600 text-white'
                  : 'bg-white border border-surface-200 text-surface-600 hover:bg-surface-50'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-surface-200 overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <i className="ri-loader-4-line animate-spin text-surface-400 text-xl" />
            </div>
          ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-200">
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Order Ref</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Product</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Plan</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Amount</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Status</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Date</th>
                <th className="text-left px-5 py-3 font-medium text-surface-500 text-xs">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((order) => (
                <tr key={order.id} className="border-b border-surface-50 hover:bg-surface-50/50">
                  <td className="px-5 py-3 font-semibold text-surface-900">{order.orderRef}</td>
                  <td className="px-5 py-3 text-surface-700">{order.productName}</td>
                  <td className="px-5 py-3 text-surface-500">{order.planName}</td>
                  <td className="px-5 py-3 font-semibold text-surface-900">${order.amount}</td>
                  <td className="px-5 py-3">
                    <span
                      className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                        order.status === 'paid'
                          ? 'bg-green-50 text-green-700'
                          : order.status === 'pending'
                          ? 'bg-amber-50 text-amber-700'
                          : 'bg-red-50 text-red-700'
                      }`}
                    >
                      {order.status}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-surface-500">{formatDate(order.createdAt)}</td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => openDetail(order)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-100 text-surface-500">
                        <i className="ri-eye-line" />
                      </button>
                      {order.status === 'paid' && (
                        <button onClick={() => downloadInvoice(order)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-100 text-surface-500">
                          <i className="ri-file-text-line" />
                        </button>
                      )}
                      {order.status === 'pending' && (
                        <button
                          onClick={() => openPayModal(order)}
                          className="px-3 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-xs font-medium transition-colors flex items-center gap-1.5">
                          <i className="ri-bank-card-line" /> Pay Now
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </div>
      </div>

      {/* Payment Modal */}
      {payModalOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { setPayModalOrder(null); setPayError(''); }} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-surface-200 flex items-center justify-between">
              <h3 className="font-semibold text-surface-900 text-sm">Complete Payment</h3>
              <button onClick={() => { setPayModalOrder(null); setPayError(''); }} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-100 text-surface-500">
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              <div className="bg-surface-50 rounded-xl p-4 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-surface-500">Order</span><span className="font-medium text-surface-900">#{payModalOrder.orderRef}</span></div>
                <div className="flex justify-between"><span className="text-surface-500">Product</span><span className="font-medium text-surface-900">{payModalOrder.productName}</span></div>
                <div className="flex justify-between"><span className="text-surface-500">Plan</span><span className="font-medium text-surface-900">{payModalOrder.planName}</span></div>
                <div className="flex justify-between pt-2 border-t border-surface-200"><span className="text-surface-700 font-semibold">Amount Due</span><span className="text-lg font-bold text-brand-600">${payModalOrder.amount}</span></div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 flex items-start gap-3">
                <i className="ri-information-line text-amber-600 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-800 leading-relaxed">
                  You'll be redirected to NOWPayments to complete your crypto payment. Your license will activate automatically once confirmed.
                </p>
              </div>

              {payError && (
                <p className="text-xs text-red-600 flex items-center gap-1"><i className="ri-error-warning-line" /> {payError}</p>
              )}
            </div>

            <div className="px-6 py-4 border-t border-surface-200 flex items-center justify-end gap-2 bg-surface-50/50">
              <button onClick={() => { setPayModalOrder(null); setPayError(''); }} className="px-4 py-2 rounded-lg border border-surface-300 text-surface-700 text-sm font-medium transition-colors">
                Cancel
              </button>
              <button
                onClick={handlePayNow}
                disabled={payingOrderId === payModalOrder.id}
                className="px-5 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 disabled:bg-surface-300 text-white text-sm font-semibold transition-colors flex items-center gap-2">
                {payingOrderId === payModalOrder.id ? (
                  <><i className="ri-loader-4-line animate-spin" /> Processing…</>
                ) : (
                  <><i className="ri-shield-check-line" /> Pay ${payModalOrder.amount}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {detailOrder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setDetailOrder(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-surface-200 flex items-center justify-between">
              <h3 className="font-semibold text-surface-900 text-sm">Order #{detailOrder.orderRef}</h3>
              <button onClick={() => setDetailOrder(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-100 text-surface-500"><i className="ri-close-line text-lg" /></button>
            </div>
            <div className="px-6 py-5 space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="bg-surface-50 rounded-lg p-3"><p className="text-xs text-surface-400">Product</p><p className="font-medium text-surface-900">{detailOrder.productName}</p></div>
                <div className="bg-surface-50 rounded-lg p-3"><p className="text-xs text-surface-400">Plan</p><p className="font-medium text-surface-900">{detailOrder.planName}</p></div>
                <div className="bg-surface-50 rounded-lg p-3"><p className="text-xs text-surface-400">Amount</p><p className="font-medium text-surface-900">${detailOrder.amount}</p></div>
                <div className="bg-surface-50 rounded-lg p-3"><p className="text-xs text-surface-400">Status</p><p className={`font-medium capitalize ${detailOrder.status === 'paid' ? 'text-green-600' : 'text-amber-600'}`}>{detailOrder.status}</p></div>
                <div className="bg-surface-50 rounded-lg p-3"><p className="text-xs text-surface-400">Date</p><p className="font-medium text-surface-900">{formatDate(detailOrder.createdAt)}</p></div>
                {detailOrder.paidAt && <div className="bg-surface-50 rounded-lg p-3"><p className="text-xs text-surface-400">Paid At</p><p className="font-medium text-surface-900">{formatDate(detailOrder.paidAt)}</p></div>}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-surface-200 flex items-center justify-end gap-2 bg-surface-50/50">
              <button onClick={() => downloadInvoice(detailOrder)} className="px-4 py-2 rounded-lg border border-surface-300 text-surface-700 text-sm font-medium hover:bg-surface-50 transition-colors flex items-center gap-2">
                <i className="ri-file-text-line" /> Download Invoice
              </button>
              <button onClick={() => setDetailOrder(null)} className="px-4 py-2 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 transition-colors">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}